import type React from "react";
import { useEffect, useState } from "react";

import { faEdit, faThumbsDown, faThumbsUp } from "@fortawesome/pro-regular-svg-icons";
import {
  faClose,
  faThumbsDown as faThumbsDownSolid,
  faThumbsUp as faThumbsUpSolid,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import invariant from "ts-invariant";

import { useApiClient } from "api/useApiClient";
import { StarsIcon } from "assets";
import { useProfileContext } from "contexts/ProfileContext";
import { BaseButton } from "shared/atoms/BaseButton";
import { IconButton } from "shared/atoms/IconButton";
import { AnalyticsService } from "utils/analytics";

interface Props {
  className?: string;
  commentId?: number;
  patientId: number;
  smartReply: string;
  smartReplyId: number;
  dismissEvent: string;
  editEvent: string;
  presentEvent: string;
  submitEvent: string;
  onEdit: (value: string) => void;
  onSubmit: (value: string) => void;
}

export const LeifGPT: React.FC<Props> = ({
  className,
  commentId,
  patientId,
  smartReply,
  smartReplyId,
  dismissEvent,
  editEvent,
  presentEvent,
  submitEvent,
  onEdit,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { profile } = useProfileContext();
  const apiClient = useApiClient();

  const [showSmartQuickReply, setShowSmartQuickReply] = useState(true);
  const [feedback, setFeedback] = useState<"positive" | "negative" | null>(null);

  invariant(profile);

  const sendFeedback = (value: "positive" | "negative") => {
    apiClient.postSmartReplyFeedback(smartReplyId, value);
  };

  const trackEvent = (event: string) => {
    AnalyticsService.track(event, {
      comment_id: commentId,
      patient_id: patientId,
      therapist_id: profile.id,
    });
  };

  useEffect(() => {
    if (showSmartQuickReply) {
      trackEvent(presentEvent);
    }
  }, [showSmartQuickReply]);

  return (
    <>
      {showSmartQuickReply ? (
        <Container className={className}>
          <Header>
            <StarsIcon />
            <HeaderText>{t("leifgpt.header_text")}</HeaderText>

            <CloseButton
              onClick={() => {
                if (feedback) sendFeedback(feedback);
                trackEvent(dismissEvent);
                setShowSmartQuickReply(false);
              }}
            >
              <FontAwesomeIcon icon={faClose} />
            </CloseButton>
          </Header>

          <SuggestedReply>{smartReply}</SuggestedReply>

          <Footer>
            <BlueIconButton
              label={t("leifgpt.edit_btn")}
              onClick={() => {
                if (feedback) sendFeedback(feedback);
                trackEvent(editEvent);
                onEdit(smartReply);
                setShowSmartQuickReply(false);
              }}
            >
              <FontAwesomeIcon icon={faEdit} style={{ height: "18px" }} />
            </BlueIconButton>

            <FeedbackButtons>
              <FeedbackButton
                aria-label={t("leifgpt.feedback_positive")}
                active={feedback === "positive"}
                onClick={() => setFeedback(feedback === "positive" ? null : "positive")}
              >
                <FontAwesomeIcon icon={feedback === "positive" ? faThumbsUpSolid : faThumbsUp} />
              </FeedbackButton>
              <FeedbackButton
                aria-label={t("leifgpt.feedback_negative")}
                active={feedback === "negative"}
                onClick={() => setFeedback(feedback === "negative" ? null : "negative")}
              >
                <FontAwesomeIcon icon={feedback === "negative" ? faThumbsDownSolid : faThumbsDown} />
              </FeedbackButton>
            </FeedbackButtons>

            <BaseButton
              variant="primary"
              icon="paper-plane"
              onClick={() => {
                if (feedback) sendFeedback(feedback);
                trackEvent(submitEvent);
                onSubmit(smartReply);
                setShowSmartQuickReply(false);
              }}
              style={{
                flexBasis: "auto",
                flexGrow: 0,
                marginLeft: "auto",
                minHeight: "auto",
                maxHeight: "40px",
                padding: "12px 24px",
              }}
            />
          </Footer>
        </Container>
      ) : null}
    </>
  );
};

const Container = styled.section`
  width: 100%;
  margin-top: 8px;
  padding-bottom: 16px;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 5px 10px 0px rgba(68, 104, 147, 0.15);
  border-radius: 8px;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  padding: 4px 0 4px 12px;
  color: ${({ theme }) => theme.colors.white};
  background: ${({ theme }) =>
    `linear-gradient(90deg, ${theme.colors.redesign.p100} 0%, ${theme.colors.redesign.b100} 100%)`};
  border-radius: 8px 8px 0 0;
`;

const HeaderText = styled.span`
  margin-left: 8px;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
`;

const CloseButton = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
  padding: 12px;
  cursor: pointer;
`;

const SuggestedReply = styled.div`
  padding: 16px 12px 8px;
  white-space: pre-wrap;
`;

const Footer = styled.footer`
  display: flex;
  justify-content: space-between;
  padding: 0 12px;
`;

const BlueIconButton = styled(IconButton)`
  color: ${({ theme }) => theme.colors.redesign.b100};

  div:nth-of-type(1) {
    height: auto;
    width: auto;
  }

  div:nth-of-type(2) {
    margin-left: 4px;
    font-size: 16px;
    line-height: normal;
  }
`;

const FeedbackButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
`;

const FeedbackButton = styled.button<{ active: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  color: ${({ theme, active }) => (active ? theme.colors.redesign.b100 : theme.colors.redesign.db50)};
  display: flex;
  align-items: center;
  font-size: 16px;

  &:hover {
    color: ${({ theme }) => theme.colors.redesign.b100};
  }
`;

export const SmartReplyIndicator: React.FC = () => {
  return (
    <Background>
      <StarsIcon />
    </Background>
  );
};

const Background = styled.div`
  margin-left: auto;

  svg {
    path {
      fill: ${({ theme }) => theme.colors.redesign.b100};
    }
  }
`;
